/**
 * Category ID to Name Mapping
 * Fixes encoding issues for Russian and other languages
 * Works across Android, iOS, and Web platforms
 * 
 * IMPORTANT: These mappings MUST match the actual database categories table!
 * Last updated: 2025-01-19 to fix category ID mismatches
 */

export interface CategoryMapping {
  [categoryId: number]: {
    [languageCode: string]: string;
  };
}

export interface CategoryDescriptionMapping {
  [categoryId: number]: {
    [languageCode: string]: string;
  };
}

export const CATEGORY_ID_MAPPING: CategoryMapping = {
  1: {
    en: 'Old Testament',
    ru: 'Ветхий Завет',
    fr: 'Ancien Testament',
    es: 'Antiguo Testamento',
    de: 'Altes Testament',
    pt: 'Antigo Testamento',
    tl: 'Lumang Tipan',
    it: 'Antico Testamento',
    am: 'ብሉይ ኪዳን',
    ko: '구약'
  },
  2: {
    en: 'Geography & Places',
    ru: 'География и места',
    fr: 'Géographie et lieux',
    es: 'Geografía y lugares',
    de: 'Geographie und Orte',
    pt: 'Geografia e lugares',
    tl: 'Heograpiya at mga lugar',
    it: 'Geografia e luoghi',
    am: 'ጂኦግራፊና ቦታዎች',
    ko: '지리와 장소'
  },
  3: {
    en: 'Kings & Leaders',
    ru: 'Цари и правители',
    fr: 'Rois et dirigeants',
    es: 'Reyes y gobernantes',
    de: 'Könige und Herrscher',
    pt: 'Reis e governantes',
    tl: 'Mga hari at pinuno',
    it: 'Re e sovrani',
    am: 'ነገሥታትና አገዛዝ',
    ko: '왕과 통치자'
  },
  4: {
    en: 'Life of Jesus',
    ru: 'Жизнь Иисуса',
    fr: 'Vie de Jésus',
    es: 'Vida de Jesús',
    de: 'Leben Jesu',
    pt: 'Vida de Jesus',
    tl: 'Buhay ni Jesus',
    it: 'Vita di Gesù',
    am: 'የኢየሱስ ሕይወት',
    ko: '예수의 생애'
  },
  5: {
    en: 'Miracles',
    ru: 'Чудеса',
    fr: 'Miracles',
    es: 'Milagros',
    de: 'Wunder',
    pt: 'Milagres',
    tl: 'Mga himala',
    it: 'Miracoli',
    am: 'ተአምራት',
    ko: '기적'
  },
  6: {
    en: 'New Testament',
    ru: 'Новый Завет',
    fr: 'Nouveau Testament',
    es: 'Nuevo Testamento',
    de: 'Neues Testament',
    pt: 'Novo Testamento',
    tl: 'Bagong Tipan',
    it: 'Nuovo Testamento',
    am: 'አዲስ ኪዳን',
    ko: '신약'
  },
  7: {
    en: 'Parables',
    ru: 'Притчи',
    fr: 'Paraboles',
    es: 'Parábolas',
    de: 'Gleichnisse',
    pt: 'Parábolas',
    tl: 'Mga talinghaga',
    it: 'Parabole',
    am: 'ምሳሌዎች',
    ko: '비유'
  },
  8: {
    en: 'Prophets',
    ru: 'Пророки',
    fr: 'Prophètes',
    es: 'Profetas',
    de: 'Propheten',
    pt: 'Profetas',
    tl: 'Mga propeta',
    it: 'Profeti',
    am: 'ነቢያት',
    ko: '선지자'
  },
  9: {
    en: 'Psalms & Wisdom',
    ru: 'Псалмы и мудрость',
    fr: 'Psaumes et sagesse',
    es: 'Salmos y sabiduría',
    de: 'Psalmen und Weisheit',
    pt: 'Salmos e sabedoria',
    tl: 'Mga awit at karunungan',
    it: 'Salmi e saggezza',
    am: 'መዝሙራትና ጥበብ',
    ko: '시편과 지혜서'
  },
  10: {
    en: 'Women of the Bible',
    ru: 'Женщины Библии',
    fr: 'Femmes de la Bible',
    es: 'Mujeres de la Biblia',
    de: 'Frauen der Bibel',
    pt: 'Mulheres da Bíblia',
    tl: 'Mga babae sa Bibliya',
    it: 'Donne della Bibbia',
    am: 'የመጽሐፍ ቅዱስ ሴቶች',
    ko: '성경의 여인들'
  },
  // Bible Books - Old Testament (IDs 12-50)
  12: { en: 'Genesis', ru: 'Бытие', fr: 'Genèse', es: 'Génesis', de: 'Genesis', pt: 'Gênesis', tl: 'Genesis', it: 'Genesi', am: 'ኦሪት ዘፍጥረት', ko: '창세기' },
  13: { en: 'Exodus', ru: 'Исход', fr: 'Exode', es: 'Éxodo', de: 'Exodus', pt: 'Êxodo', tl: 'Exodus', it: 'Esodo', am: 'ኦሪት ዘጸአት', ko: '출애굽기' },
  14: { en: 'Leviticus', ru: 'Левит', fr: 'Lévitique', es: 'Levítico', de: 'Levitikus', pt: 'Levítico', tl: 'Leviticus', it: 'Levitico', am: 'ኦሪት ዘሌዋውያን', ko: '레위기' },
  15: { en: 'Numbers', ru: 'Числа', fr: 'Nombres', es: 'Números', de: 'Numeri', pt: 'Números', tl: 'Numbers', it: 'Numeri', am: 'ኦሪት ዘኍልቈ', ko: '민수기' },
  16: { en: 'Deuteronomy', ru: 'Второзаконие', fr: 'Deutéronome', es: 'Deuteronomio', de: 'Deuteronomium', pt: 'Deuteronômio', tl: 'Deuteronomy', it: 'Deuteronomio', am: 'ኦሪት ዘዳግም', ko: '신명기' },
  17: { en: 'Joshua', ru: 'Иисус Навин', fr: 'Josué', es: 'Josué', de: 'Josua', pt: 'Josué', tl: 'Joshua', it: 'Giosuè', am: 'መጽሐፈ ኢያሱ', ko: '여호수아' },
  18: { en: 'Judges', ru: 'Судьи', fr: 'Juges', es: 'Jueces', de: 'Richter', pt: 'Juízes', tl: 'Judges', it: 'Giudici', am: 'መጽሐፈ መሳፍንት', ko: '사사기' },
  19: { en: 'Ruth', ru: 'Руфь', fr: 'Ruth', es: 'Rut', de: 'Rut', pt: 'Rute', tl: 'Ruth', it: 'Rut', am: 'መጽሐፈ ሩት', ko: '룻기' },
  20: { en: '1 Samuel', ru: '1 Царств', fr: '1 Samuel', es: '1 Samuel', de: '1. Samuel', pt: '1 Samuel', tl: '1 Samuel', it: '1 Samuele', am: '1ኛ መጽሐፈ ሳሙኤል', ko: '사무엘상' },
  21: { en: '2 Samuel', ru: '2 Царств', fr: '2 Samuel', es: '2 Samuel', de: '2. Samuel', pt: '2 Samuel', tl: '2 Samuel', it: '2 Samuele', am: '2ኛ መጽሐፈ ሳሙኤል', ko: '사무엘하' },
  22: { en: '1 Kings', ru: '3 Царств', fr: '1 Rois', es: '1 Reyes', de: '1. Könige', pt: '1 Reis', tl: '1 Kings', it: '1 Re', am: '1ኛ መጽሐፈ ነገሥት', ko: '열왕기상' },
  23: { en: '2 Kings', ru: '4 Царств', fr: '2 Rois', es: '2 Reyes', de: '2. Könige', pt: '2 Reis', tl: '2 Kings', it: '2 Re', am: '2ኛ መጽሐፈ ነገሥት', ko: '열왕기하' },
  24: { en: '1 Chronicles', ru: '1 Паралипоменон', fr: '1 Chroniques', es: '1 Crónicas', de: '1. Chronik', pt: '1 Crônicas', tl: '1 Chronicles', it: '1 Cronache', am: '1ኛ መጽሐፈ ዜና', ko: '역대상' },
  25: { en: '2 Chronicles', ru: '2 Паралипоменон', fr: '2 Chroniques', es: '2 Crónicas', de: '2. Chronik', pt: '2 Crônicas', tl: '2 Chronicles', it: '2 Cronache', am: '2ኛ መጽሐፈ ዜና', ko: '역대하' },
  26: { en: 'Ezra', ru: 'Ездра', fr: 'Esdras', es: 'Esdras', de: 'Esra', pt: 'Esdras', tl: 'Ezra', it: 'Esdra', am: 'መጽሐፈ ዕዝራ', ko: '에스라' },
  27: { en: 'Nehemiah', ru: 'Неемия', fr: 'Néhémie', es: 'Nehemías', de: 'Nehemia', pt: 'Neemias', tl: 'Nehemiah', it: 'Neemia', am: 'መጽሐፈ ኔህምያ', ko: '느헤미야' },
  28: { en: 'Esther', ru: 'Есфирь', fr: 'Esther', es: 'Ester', de: 'Ester', pt: 'Ester', tl: 'Esther', it: 'Ester', am: 'መጽሐፈ አስቴር', ko: '에스더' },
  29: { en: 'Job', ru: 'Иов', fr: 'Job', es: 'Job', de: 'Hiob', pt: 'Jó', tl: 'Job', it: 'Giobbe', am: 'መጽሐፈ ኢዮብ', ko: '욥기' },
  30: { en: 'Psalms', ru: 'Псалтирь', fr: 'Psaumes', es: 'Salmos', de: 'Psalmen', pt: 'Salmos', tl: 'Psalms', it: 'Salmi', am: 'መዝሙረ ዳዊት', ko: '시편' },
  31: { en: 'Proverbs', ru: 'Притчи', fr: 'Proverbes', es: 'Proverbios', de: 'Sprüche', pt: 'Provérbios', tl: 'Proverbs', it: 'Proverbi', am: 'መጽሐፈ ምሳሌ', ko: '잠언' },
  32: { en: 'Ecclesiastes', ru: 'Екклесиаст', fr: 'Ecclésiaste', es: 'Eclesiastés', de: 'Prediger', pt: 'Eclesiastes', tl: 'Ecclesiastes', it: 'Ecclesiaste', am: 'መጽሐፈ መክብብ', ko: '전도서' },
  33: { en: 'Song of Solomon', ru: 'Песнь Песней', fr: 'Cantique des cantiques', es: 'Cantar de los Cantares', de: 'Hoheslied', pt: 'Cantares', tl: 'Song of Solomon', it: 'Cantico dei Cantici', am: 'መኃልየ መኃልይ', ko: '아가' },
  34: { en: 'Isaiah', ru: 'Исаия', fr: 'Ésaïe', es: 'Isaías', de: 'Jesaja', pt: 'Isaías', tl: 'Isaiah', it: 'Isaia', am: 'መጽሐፈ ኢሳይያስ', ko: '이사야' },
  35: { en: 'Jeremiah', ru: 'Иеремия', fr: 'Jérémie', es: 'Jeremías', de: 'Jeremia', pt: 'Jeremias', tl: 'Jeremiah', it: 'Geremia', am: 'መጽሐፈ ኤርምያስ', ko: '예레미야' },
  36: { en: 'Lamentations', ru: 'Плач Иеремии', fr: 'Lamentations', es: 'Lamentaciones', de: 'Klagelieder', pt: 'Lamentações', tl: 'Lamentations', it: 'Lamentazioni', am: 'ሰቆቃዌ ኤርምያስ', ko: '예레미야애가' },
  37: { en: 'Ezekiel', ru: 'Иезекииль', fr: 'Ézéchiel', es: 'Ezequiel', de: 'Hesekiel', pt: 'Ezequiel', tl: 'Ezekiel', it: 'Ezechiele', am: 'መጽሐፈ ሕዝቅኤል', ko: '에스겔' },
  38: { en: 'Daniel', ru: 'Даниил', fr: 'Daniel', es: 'Daniel', de: 'Daniel', pt: 'Daniel', tl: 'Daniel', it: 'Daniele', am: 'መጽሐፈ ዳንኤል', ko: '다니엘' },
  39: { en: 'Hosea', ru: 'Осия', fr: 'Osée', es: 'Oseas', de: 'Hosea', pt: 'Oséias', tl: 'Hosea', it: 'Osea', am: 'መጽሐፈ ሆሴዕ', ko: '호세아' },
  40: { en: 'Joel', ru: 'Иоиль', fr: 'Joël', es: 'Joel', de: 'Joel', pt: 'Joel', tl: 'Joel', it: 'Gioele', am: 'መጽሐፈ ኢዮኤል', ko: '요엘' },
  41: { en: 'Amos', ru: 'Амос', fr: 'Amos', es: 'Amós', de: 'Amos', pt: 'Amós', tl: 'Amos', it: 'Amos', am: 'መጽሐፈ አሞጽ', ko: '아모스' },
  42: { en: 'Obadiah', ru: 'Авдий', fr: 'Abdias', es: 'Abdías', de: 'Obadja', pt: 'Obadias', tl: 'Obadiah', it: 'Abdia', am: 'መጽሐፈ አብድዩ', ko: '오바댜' },
  43: { en: 'Jonah', ru: 'Иона', fr: 'Jonas', es: 'Jonás', de: 'Jona', pt: 'Jonas', tl: 'Jonah', it: 'Giona', am: 'መጽሐፈ ዮናስ', ko: '요나' },
  44: { en: 'Micah', ru: 'Михей', fr: 'Michée', es: 'Miqueas', de: 'Micha', pt: 'Miquéias', tl: 'Micah', it: 'Michea', am: 'መጽሐፈ ሚክያስ', ko: '미가' },
  45: { en: 'Nahum', ru: 'Наум', fr: 'Nahum', es: 'Nahúm', de: 'Nahum', pt: 'Naum', tl: 'Nahum', it: 'Nahum', am: 'መጽሐፈ ናሆም', ko: '나훔' },
  46: { en: 'Habakkuk', ru: 'Аввакум', fr: 'Habacuc', es: 'Habacuc', de: 'Habakuk', pt: 'Habacuque', tl: 'Habakkuk', it: 'Abacuc', am: 'መጽሐፈ ዕንባቆም', ko: '하박국' },
  47: { en: 'Zephaniah', ru: 'Софония', fr: 'Sophonie', es: 'Sofonías', de: 'Zefanja', pt: 'Sofonias', tl: 'Zephaniah', it: 'Sofonia', am: 'መጽሐፈ ሶፎንያስ', ko: '스바냐' },
  48: { en: 'Haggai', ru: 'Аггей', fr: 'Aggée', es: 'Hageo', de: 'Haggai', pt: 'Ageu', tl: 'Haggai', it: 'Aggeo', am: 'መጽሐፈ ሐጌ', ko: '학개' },
  49: { en: 'Zechariah', ru: 'Захария', fr: 'Zacharie', es: 'Zacarías', de: 'Sacharja', pt: 'Zacarias', tl: 'Zechariah', it: 'Zaccaria', am: 'መጽሐፈ ዘካርያስ', ko: '스가랴' },
  50: { en: 'Malachi', ru: 'Малахия', fr: 'Malachie', es: 'Malaquías', de: 'Maleachi', pt: 'Malaquias', tl: 'Malachi', it: 'Malachia', am: 'መጽሐፈ ሚልክያስ', ko: '말라기' },
  // Bible Books - New Testament (IDs 51-77)
  51: { en: 'Matthew', ru: 'От Матфея', fr: 'Matthieu', es: 'Mateo', de: 'Matthäus', pt: 'Mateus', tl: 'Matthew', it: 'Matteo', am: 'ወንጌል ዘማትያስ', ko: '마태복음' },
  52: { en: 'Mark', ru: 'От Марка', fr: 'Marc', es: 'Marcos', de: 'Markus', pt: 'Marcos', tl: 'Mark', it: 'Marco', am: 'ወንጌል ዘማርቆስ', ko: '마가복음' },
  53: { en: 'Luke', ru: 'От Луки', fr: 'Luc', es: 'Lucas', de: 'Lukas', pt: 'Lucas', tl: 'Luke', it: 'Luca', am: 'ወንጌል ዘሉቃስ', ko: '누가복음' },
  54: { en: 'John', ru: 'От Иоанна', fr: 'Jean', es: 'Juan', de: 'Johannes', pt: 'João', tl: 'John', it: 'Giovanni', am: 'ወንጌል ዘዮሐንስ', ko: '요한복음' },
  55: { en: 'Acts', ru: 'Деяния', fr: 'Actes', es: 'Hechos', de: 'Apostelgeschichte', pt: 'Atos', tl: 'Acts', it: 'Atti', am: 'ሐዋርያት', ko: '사도행전' },
  56: { en: 'Romans', ru: 'К Римлянам', fr: 'Romains', es: 'Romanos', de: 'Römer', pt: 'Romanos', tl: 'Romans', it: 'Romani', am: 'ሮሜ', ko: '로마서' },
  57: { en: '1 Corinthians', ru: '1 Коринфянам', fr: '1 Corinthiens', es: '1 Corintios', de: '1. Korinther', pt: '1 Coríntios', tl: '1 Corinthians', it: '1 Corinzi', am: '1ኛ ቆሮንቶስ', ko: '고린도전서' },
  58: { en: '2 Corinthians', ru: '2 Коринфянам', fr: '2 Corinthiens', es: '2 Corintios', de: '2. Korinther', pt: '2 Coríntios', tl: '2 Corinthians', it: '2 Corinzi', am: '2ኛ ቆሮንቶስ', ko: '고린도후서' },
  59: { en: 'Galatians', ru: 'К Галатам', fr: 'Galates', es: 'Gálatas', de: 'Galater', pt: 'Gálatas', tl: 'Galatians', it: 'Galati', am: 'ገላትያ', ko: '갈라디아서' },
  60: { en: 'Ephesians', ru: 'К Ефесянам', fr: 'Éphésiens', es: 'Efesios', de: 'Epheser', pt: 'Efésios', tl: 'Ephesians', it: 'Efesini', am: 'ኤፌሶን', ko: '에베소서' },
  61: { en: 'Philippians', ru: 'К Филиппийцам', fr: 'Philippiens', es: 'Filipenses', de: 'Philipper', pt: 'Filipenses', tl: 'Philippians', it: 'Filippesi', am: 'ፊልጵስዩስ', ko: '빌립보서' },
  62: { en: 'Colossians', ru: 'К Колоссянам', fr: 'Colossiens', es: 'Colosenses', de: 'Kolosser', pt: 'Colossenses', tl: 'Colossians', it: 'Colossesi', am: 'ቆላስይስ', ko: '골로새서' },
  63: { en: '1 Thessalonians', ru: '1 Фессалоникийцам', fr: '1 Thessaloniciens', es: '1 Tesalonicenses', de: '1. Thessalonicher', pt: '1 Tessalonicenses', tl: '1 Thessalonians', it: '1 Tessalonicesi', am: '1ኛ ተሰሎንቄ', ko: '데살로니가전서' },
  64: { en: '2 Thessalonians', ru: '2 Фессалоникийцам', fr: '2 Thessaloniciens', es: '2 Tesalonicenses', de: '2. Thessalonicher', pt: '2 Tessalonicenses', tl: '2 Thessalonians', it: '2 Tessalonicesi', am: '2ኛ ተሰሎንቄ', ko: '데살로니가후서' },
  65: { en: '1 Timothy', ru: '1 Тимофею', fr: '1 Timothée', es: '1 Timoteo', de: '1. Timotheus', pt: '1 Timóteo', tl: '1 Timothy', it: '1 Timoteo', am: '1ኛ ጢሞቴዎስ', ko: '디모데전서' },
  66: { en: '2 Timothy', ru: '2 Тимофею', fr: '2 Timothée', es: '2 Timoteo', de: '2. Timotheus', pt: '2 Timóteo', tl: '2 Timothy', it: '2 Timoteo', am: '2ኛ ጢሞቴዎስ', ko: '디모데후서' },
  67: { en: 'Titus', ru: 'К Титу', fr: 'Tite', es: 'Tito', de: 'Titus', pt: 'Tito', tl: 'Titus', it: 'Tito', am: 'ጢቶስ', ko: '디도서' },
  68: { en: 'Philemon', ru: 'К Филимону', fr: 'Philémon', es: 'Filemón', de: 'Philemon', pt: 'Filemom', tl: 'Philemon', it: 'Filemone', am: 'ፊልሞና', ko: '빌레몬서' },
  69: { en: 'Hebrews', ru: 'К Евреям', fr: 'Hébreux', es: 'Hebreos', de: 'Hebräer', pt: 'Hebreus', tl: 'Hebrews', it: 'Ebrei', am: 'ዕብራውያን', ko: '히브리서' },
  70: { en: 'James', ru: 'Иаков', fr: 'Jacques', es: 'Santiago', de: 'Jakobus', pt: 'Tiago', tl: 'James', it: 'Giacomo', am: 'ያዕቆብ', ko: '야고보서' },
  71: { en: '1 Peter', ru: '1 Петра', fr: '1 Pierre', es: '1 Pedro', de: '1. Petrus', pt: '1 Pedro', tl: '1 Peter', it: '1 Pietro', am: '1ኛ ጴጥሮስ', ko: '베드로전서' },
  72: { en: '2 Peter', ru: '2 Петра', fr: '2 Pierre', es: '2 Pedro', de: '2. Petrus', pt: '2 Pedro', tl: '2 Peter', it: '2 Pietro', am: '2ኛ ጴጥሮስ', ko: '베드로후서' },
  73: { en: '1 John', ru: '1 Иоанна', fr: '1 Jean', es: '1 Juan', de: '1. Johannes', pt: '1 João', tl: '1 John', it: '1 Giovanni', am: '1ኛ ዮሐንስ', ko: '요한일서' },
  74: { en: '2 John', ru: '2 Иоанна', fr: '2 Jean', es: '2 Juan', de: '2. Johannes', pt: '2 João', tl: '2 John', it: '2 Giovanni', am: '2ኛ ዮሐንስ', ko: '요한이서' },
  75: { en: '3 John', ru: '3 Иоанна', fr: '3 Jean', es: '3 Juan', de: '3. Johannes', pt: '3 João', tl: '3 John', it: '3 Giovanni', am: '3ኛ ዮሐንስ', ko: '요한삼서' },
  76: { en: 'Jude', ru: 'Иуда', fr: 'Jude', es: 'Judas', de: 'Judas', pt: 'Judas', tl: 'Jude', it: 'Giuda', am: 'አይሁዳ', ko: '유다서' },
  77: { en: 'Revelation', ru: 'Откровение', fr: 'Apocalypse', es: 'Apocalipsis', de: 'Offenbarung', pt: 'Apocalipse', tl: 'Revelation', it: 'Apocalisse', am: 'የዮሐንስ ራዕይ', ko: '요한계시록' },
  100: { en: 'Early Church History', ru: 'История ранней Церкви', fr: 'Histoire de l\'Église primitive', es: 'Historia de la Iglesia primitiva', de: 'Frühe Kirchengeschichte', pt: 'História da Igreja primitiva', tl: 'Kasaysayan ng Unang Simbahan', it: 'Storia della Chiesa primitiva', am: 'የመጀመሪያው ቤተክርስቲያን ታሪክ', ko: '초대 교회 역사' },
  101: { en: 'Church Traditions & Movements', ru: 'Церковные традиции и движения', fr: 'Traditions et mouvements ecclésiastiques', es: 'Tradiciones y movimientos de la Iglesia', de: 'Kirchliche Traditionen und Bewegungen', pt: 'Tradições e movimentos da Igreja', tl: 'Mga Tradisyon at Kilusan ng Simbahan', it: 'Tradizioni e movimenti ecclesiali', am: 'የቤተክርስቲያን ወጎችና እንቅስቃሴዎች', ko: '교회 전통과 운동' },
  112: { en: 'Apostolic & Sub-Apostolic Era', ru: 'Апостольская и субапостольская эра', fr: 'Ère apostolique et sub-apostolique', es: 'Era apostólica y subapostólica', de: 'Apostolische und subapostolische Ära', pt: 'Era apostólica e subapostólica', tl: 'Panahon ng Apostoliko at Sub-Apostoliko', it: 'Era apostolica e subapostolica', am: 'የሐዋርያት እና ከሐዋርያት በኋላ ዘመን', ko: '사도 및 사도 이후 시대' },
  113: { en: 'Apologists & Martyrs', ru: 'Апологеты и мученики', fr: 'Apologistes et martyrs', es: 'Apologistas y mártires', de: 'Apologeten und Märtyrer', pt: 'Apologistas e mártires', tl: 'Mga Apologista at Martir', it: 'Apologisti e martiri', am: 'ተሟጋቾችና ሰማዕታት', ko: '변증가와 순교자' },
  114: { en: 'Councils & Creeds', ru: 'Соборы и символы веры', fr: 'Conciles et crédos', es: 'Concilios y credos', de: 'Konzile und Glaubensbekenntnisse', pt: 'Concílios e credos', tl: 'Mga Konseho at Paniniwala', it: 'Concili e credo', am: 'ጉባኤዎችና የእምነት መግለጫዎች', ko: '공의회와 신조' },
  115: { en: 'Church Fathers & Theologians', ru: 'Отцы Церкви и богословы', fr: 'Pères de l\'Église et théologiens', es: 'Padres de la Iglesia y teólogos', de: 'Kirchenväter und Theologen', pt: 'Pais da Igreja e teólogos', tl: 'Mga Ama ng Simbahan at Teologo', it: 'Padri della Chiesa e teologi', am: 'የቤተክርስቲያን አባቶችና የእምነት ምሁራን', ko: '교부와 신학자' },
  116: { en: 'Canon & Scripture Formation', ru: 'Канон и формирование Писания', fr: 'Canon et formation des Écritures', es: 'Canon y formación de las Escrituras', de: 'Kanon und Schriftbildung', pt: 'Cânon e formação das Escrituras', tl: 'Canon at Pagbuo ng Kasulatan', it: 'Canone e formazione delle Scritture', am: 'መጽሐፍ ቅዱስ መመዘኛና አደረጃጀት', ko: '정경과 성경 형성' },
  117: { en: 'Worship & Liturgy Origins', ru: 'Происхождение богослужения и литургии', fr: 'Origines du culte et de la liturgie', es: 'Orígenes del culto y la liturgia', de: 'Ursprünge von Gottesdienst und Liturgie', pt: 'Origens do culto e da liturgia', tl: 'Pinagmulan ng Pagsamba at Liturhiya', it: 'Origini del culto e della liturgia', am: 'የአምልኮና የሥርዓት መነሻ', ko: '예배와 전례의 기원' },
  118: { en: 'Monastic & Desert Traditions', ru: 'Монашеские и пустынные традиции', fr: 'Traditions monastiques et du désert', es: 'Tradiciones monásticas y del desierto', de: 'Mönchstum und Wüstentraditionen', pt: 'Tradições monásticas e do deserto', tl: 'Tradisyon ng Monastiko at Disyerto', it: 'Tradizioni monastiche e del deserto', am: 'የመነኮሳትና የበረሃ ወጎች', ko: '수도원과 사막 전통' }, 
  119: { en: 'Catholic Tradition', ru: 'Католическая традиция', fr: 'Tradition catholique', es: 'Tradición católica', de: 'Katholische Tradition', pt: 'Tradição católica', tl: 'Tradisyong Katoliko', it: 'Tradizione cattolica', am: 'የካቶሊክ ወግ', ko: '가톨릭 전통' },
  120: { en: 'Orthodox Tradition', ru: 'Православная традиция', fr: 'Tradition orthodoxe', es: 'Tradición ortodoxa', de: 'Orthodoxe Tradition', pt: 'Tradição ortodoxa', tl: 'Tradisyong Ortodokso', it: 'Tradizione ortodossa', am: 'የኦርቶዶክስ ወግ', ko: '정교회 전통' },
  121: { en: 'Evangelical Movement', ru: 'Евангельское движение', fr: 'Mouvement évangélique', es: 'Movimiento evangélico', de: 'Evangelikale Bewegung', pt: 'Movimento evangélico', tl: 'Kilusang Ebanhelikal', it: 'Movimento evangelico', am: 'የወንጌላዊ እንቅስቃሴ', ko: '복음주의 운동' },
  122: { en: 'Anglican Tradition', ru: 'Англиканская традиция', fr: 'Tradition anglicane', es: 'Tradición anglicana', de: 'Anglikanische Tradition', pt: 'Tradição anglicana', tl: 'Tradisyong Anglikano', it: 'Tradizione anglicana', am: 'የአንግሊካን ወግ', ko: '성공회 전통' },
  123: { en: 'Lutheran Tradition', ru: 'Лютеранская традиция', fr: 'Tradition luthérienne', es: 'Tradición luterana', de: 'Lutherische Tradition', pt: 'Tradição luterana', tl: 'Tradisyong Luterano', it: 'Tradizione luterana', am: 'የሉተራን ወግ', ko: '루터교 전통' },
  124: { en: 'Methodist Movement', ru: 'Методистское движение', fr: 'Mouvement méthodiste', es: 'Movimiento metodista', de: 'Methodistische Bewegung', pt: 'Movimento metodista', tl: 'Kilusang Metodista', it: 'Movimento metodista', am: 'የሜቶዲስት እንቅስቃሴ', ko: '감리교 운동' },
  125: { en: 'Presbyterian Tradition', ru: 'Пресвитерианская традиция', fr: 'Tradition presbytérienne', es: 'Tradición presbiteriana', de: 'Presbyterianische Tradition', pt: 'Tradição presbiteriana', tl: 'Tradisyong Presbiteryano', it: 'Tradizione presbiteriana', am: 'የፕሬስቢተሪያን ወግ', ko: '장로교 전통' },
  126: { en: 'Seventh-Day Adventist', ru: 'Адвентисты седьмого дня', fr: 'Adventiste du septième jour', es: 'Adventista del Séptimo Día', de: 'Siebenten-Tags-Adventisten', pt: 'Adventista do Sétimo Dia', tl: 'Adventista ng Ikapitong Araw', it: 'Avventista del Settimo Giorno', am: 'የሰባተኛው ቀን አድቬንቲስት', ko: '제칠일안식일예수재림교' },
  127: { en: 'Pentecostal & Charismatic', ru: 'Пятидесятники и харизматы', fr: 'Pentecôtiste et charismatique', es: 'Pentecostal y carismático', de: 'Pfingstler und Charismatiker', pt: 'Pentecostal e carismático', tl: 'Pentecostal at Charismatic', it: 'Pentecostale e carismatico', am: 'ጴንጤቆስጤና ካሪዝማቲክ', ko: '오순절과 은사주의' },
  128: { en: 'Zaoga / Forward in Faith', ru: 'Заога / Вперед в вере', fr: 'Zaoga / En avant dans la foi', es: 'Zaoga / Adelante en la fe', de: 'Zaoga / Vorwärts im Glauben', pt: 'Zaoga / Avançar na fé', tl: 'Zaoga / Sulong sa Pananampalataya', it: 'Zaoga / Avanti nella fede', am: 'ዛኦጋ / በእምነት ወደፊት', ko: '자오가 / 믿음으로 전진' }
};

export const CATEGORY_DESCRIPTION_MAPPING: CategoryDescriptionMapping = {
  1: {
    en: 'Books and stories from before the life of Jesus',
    ru: 'Истории, персонажи и события из Ветхого Завета',
    fr: 'Histoires, personnages et événements de l\'Ancien Testament',
    es: 'Historias, personajes y eventos del Antiguo Testamento',
    de: 'Geschichten, Charaktere und Ereignisse aus dem Alten Testament',
    pt: 'Histórias, personagens e eventos do Antigo Testamento',
    tl: 'Mga kuwento, tauhan, at pangyayari mula sa Lumang Tipan',
    it: 'Storie, personaggi ed eventi dell\'Antico Testamento',
    am: 'ከብሉይ ኪዳን ታሪኮች፣ ገፀ-ባህሪያት እና ክስተቶች',
    ko: '구약의 이야기, 인물, 사건'
  },
  2: {
    en: 'Locations, lands, and journeys in the Bible',
    ru: 'Места и регионы библейских времен',
    fr: 'Lieux et régions des temps bibliques',
    es: 'Lugares y regiones de los tiempos bíblicos',
    de: 'Orte und Regionen biblischer Zeiten',
    pt: 'Lugares e regiões dos tempos bíblicos',
    tl: 'Mga lugar at rehiyon ng panahong biblical',
    it: 'Luoghi e regioni dei tempi biblici',
    am: 'የመጽሐፍ ቅዱስ ዘመን ቦታዎችና አካባቢዎች',
    ko: '성경 시대의 장소와 지역'
  },
  3: {
    en: 'Rulers and prominent figures',
    ru: 'Правители Израиля и других народов',
    fr: 'Dirigeants d\'Israël et d\'autres nations',
    es: 'Gobernantes de Israel y otras naciones',
    de: 'Herrscher Israels und anderer Nationen',
    pt: 'Governantes de Israel e outras nações',
    tl: 'Mga pinuno ng Israel at iba pang mga bansa',
    it: 'Sovrani di Israele e altre nazioni',
    am: 'የእስራኤልና የሌሎች አህዛብ አገዛዦች',
    ko: '이스라엘과 다른 나라들의 통치자'
  },
  4: {
    en: 'Events from the life of Jesus Christ',
    ru: 'Жизнь Иисуса, апостолы и ранняя христианская церковь',
    fr: 'Vie de Jésus, apôtres et église chrétienne primitive',
    es: 'Vida de Jesús, apóstoles e iglesia cristiana primitiva',
    de: 'Leben Jesu, Apostel und frühe christliche Kirche',
    pt: 'Vida de Jesus, apóstolos e igreja cristã primitiva',
    tl: 'Buhay ni Jesus, mga apostol, at maaga ng simbahang Kristiyano',
    it: 'Vita di Gesù, apostoli e chiesa cristiana primitiva',
    am: 'የኢየሱስ ሕይወት፣ ሐዋርያት እና የመጀመሪያው የክርስቲያን ቤተክርስቲያን',
    ko: '예수의 생애, 사도들, 초기 기독교 교회'
  },
  5: {
    en: 'Supernatural acts and wonders',
    ru: 'Сверхъестественные дела Божьи',
    fr: 'Actes surnaturels de Dieu',
    es: 'Actos sobrenaturales de Dios',
    de: 'Übernatürliche Taten Gottes',
    pt: 'Atos sobrenaturais de Deus',
    tl: 'Mga supernatural na gawa ng Diyos',
    it: 'Atti soprannaturali di Dio',
    am: 'የእግዚአብሔር ከተፈጥሮ በላይ የሆኑ ተግባራት',
    ko: '하나님의 초자연적 행위'
  },
  6: {
    en: 'General New Testament content',
    ru: 'Учения Иисуса через истории',
    fr: 'Enseignements de Jésus à travers des histoires',
    es: 'Enseñanzas de Jesús a través de historias',
    de: 'Jesu Lehren durch Geschichten',
    pt: 'Ensinamentos de Jesus através de histórias',
    tl: 'Mga turo ni Jesus sa pamamagitan ng mga kuwento',
    it: 'Insegnamenti di Gesù attraverso storie',
    am: 'የኢየሱስ በተረቶች የሚሰጣቸው ትምህርቶች',
    ko: '이야기를 통한 예수의 가르침'
  },
  7: {
    en: 'Stories and lessons told by Jesus',
    ru: 'Божьи вестники и их послания',
    fr: 'Les messagers de Dieu et leurs messages',
    es: 'Los mensajeros de Dios y sus mensajes',
    de: 'Gottes Boten und ihre Botschaften',
    pt: 'Os mensageiros de Deus e suas mensagens',
    tl: 'Mga mensahero ng Diyos at kanilang mga mensahe',
    it: 'I messaggeri di Dio e i loro messaggi',
    am: 'የእግዚአብሔር መልእክተኞች እና መልእክቶቻቸው',
    ko: '하나님의 메신저들과 그들의 메시지'
  },
  8: {
    en: 'Major and minor prophets',
    ru: 'Замечательные женщины Священного Писания',
    fr: 'Femmes remarquables de l\'Écriture',
    es: 'Mujeres notables de las Escrituras',
    de: 'Bemerkenswerte Frauen der Schrift',
    pt: 'Mulheres notáveis das Escrituras',
    tl: 'Mga kahanga-hangang babae sa Kasulatan',
    it: 'Donne straordinarie delle Scritture',
    am: 'የመጽሐፍ ቅዱስ አስደናቂ ሴቶች',
    ko: '성경의 놀라운 여인들'
  },
  9: {
    en: 'Poetry and wisdom literature',
    ru: 'Вопросы по различным темам из всей Библии',
    fr: 'Questions couvrant divers sujets de toute la Bible',
    es: 'Preguntas que cubren varios temas de toda la Biblia',
    de: 'Fragen zu verschiedenen Themen aus der ganzen Bibel',
    pt: 'Perguntas cobrindo vários tópicos de toda a Bíblia',
    tl: 'Mga tanong na sumasaklaw sa iba\'t ibang paksa mula sa buong Bibliya',
    it: 'Domande che coprono vari argomenti di tutta la Bibbia',
    am: 'ከመላው መጽሐፍ ቅዱስ የተለያዩ ጉዳዮችን የሚሸፍኑ ጥያቄዎች',
    ko: '성경 전체의 다양한 주제를 다루는 질문'
  },
  10: {
    en: 'Notable women and their stories',
    ru: 'Известные люди и их истории из Библии',
    fr: 'Personnes célèbres et leurs histoires de la Bible',
    es: 'Personas famosas y sus historias de la Biblia',
    de: 'Berühmte Menschen und ihre Geschichten aus der Bibel',
    pt: 'Pessoas famosas e suas histórias da Bíblia',
    tl: 'Mga sikat na tao at kanilang mga kuwento mula sa Bibliya',
    it: 'Persone famose e le loro storie dalla Bibbia',
    am: 'ከመጽሐፍ ቅዱስ ተወዳጅ ሰዎችና ታሪኮቻቸው',
    ko: '성경의 유명한 인물들과 그들의 이야기'
  },
  100: {
    en: 'Study the first 600 years of Christianity - from the apostles to the early councils',
    ru: 'Изучите первые 600 лет христианства - от апостолов до ранних соборов',
    fr: 'Étudiez les 600 premières années du christianisme - des apôtres aux premiers conciles',
    es: 'Estudia los primeros 600 años del cristianismo - desde los apóstoles hasta los primeros concilios',
    de: 'Studieren Sie die ersten 600 Jahre des Christentums - von den Aposteln bis zu den frühen Konzilen',
    pt: 'Estude os primeiros 600 anos do cristianismo - dos apóstolos aos primeiros concílios',
    tl: 'Pag-aralan ang unang 600 taon ng Kristiyanismo - mula sa mga apostol hanggang sa mga unang konseho',
    it: 'Studia i primi 600 anni del cristianesimo - dagli apostoli ai primi concili',
    am: 'የመጀመሪያዎቹን 600 ዓመታት የክርስትና - ከሐዋርያት እስከ ቀደምት ጉባኤዎች ያጥኑ',
    ko: '기독교의 첫 600년을 공부하세요 - 사도들부터 초기 공의회까지'
  },
  101: {
    en: 'Discover the rich diversity of Christian denominations and their unique contributions to faith',
    ru: 'Откройте для себя богатое разнообразие христианских конфессий и их уникальный вклад в веру',
    fr: 'Découvrez la riche diversité des dénominations chrétiennes et leurs contributions uniques à la foi',
    es: 'Descubre la rica diversidad de denominaciones cristianas y sus contribuciones únicas a la fe',
    de: 'Entdecken Sie die reiche Vielfalt christlicher Konfessionen und ihre einzigartigen Beiträge zum Glauben',
    pt: 'Descubra a rica diversidade de denominações cristãs e suas contribuições únicas para a fé',
    tl: 'Tuklasin ang mayamang pagkakaiba-iba ng mga denominasyong Kristiyano at ang kanilang natatanging kontribusyon sa pananampalataya',
    it: 'Scopri la ricca diversità delle denominazioni cristiane e i loro contributi unici alla fede',
    am: 'የክርስትና ቤተ እምነቶች ልዩነትና ለእምነት ያደረጉትን ልዩ አስተዋፅዖ ያግኙ',
    ko: '기독교 교파의 풍부한 다양성과 신앙에 대한 독특한 기여를 발견하세요'
  }
};

/**
 * Get category name by ID and language, with fallback support
 * @param categoryId - Database category ID
 * @param languageCode - Language code (e.g., 'ru', 'en')
 * @param fallbackName - Original name from database (used as last resort)
 * @returns Properly encoded category name
 */
export const getCategoryName = (
  categoryId: number, 
  languageCode: string, 
  fallbackName?: string
): string => {
  const categoryTranslation = CATEGORY_ID_MAPPING[categoryId]?.[languageCode];
  
  if (categoryTranslation) {
    return categoryTranslation;
  }
  
  // Fallback to English
  const englishFallback = CATEGORY_ID_MAPPING[categoryId]?.['en'];
  if (englishFallback) {
    return englishFallback;
  }
  
  // Last resort: use provided fallback or generic name
  return fallbackName || `Category ${categoryId}`;
};

/**
 * Get category description by ID and language, with fallback support
 * @param categoryId - Database category ID
 * @param languageCode - Language code (e.g., 'ru', 'en')
 * @param fallbackDescription - Original description from database (used as last resort)
 * @returns Properly encoded category description
 */
export const getCategoryDescription = (
  categoryId: number, 
  languageCode: string, 
  fallbackDescription?: string
): string => {
  const categoryDescription = CATEGORY_DESCRIPTION_MAPPING[categoryId]?.[languageCode];
  
  if (categoryDescription) {
    return categoryDescription;
  }
  
  // Fallback to English
  const englishFallback = CATEGORY_DESCRIPTION_MAPPING[categoryId]?.['en'];
  if (englishFallback) {
    return englishFallback;
  }
  
  // Last resort: use provided fallback or generic description
  return fallbackDescription || `Description for category ${categoryId}`;
};

/**
 * Check if a category ID represents a main category with subcategories
 * This includes Testament categories (1, 6) and Church History categories (100, 101)
 * @param categoryId - Database category ID
 * @returns boolean indicating if it's a main category that should show subcategories
 */
export const isMainCategoryWithSubcategories = (categoryId: number): boolean => {
  // Testament categories (Old Testament = 1, New Testament = 6)
  if (categoryId === 1 || categoryId === 6) {
    return true;
  }
  
  // Church History main categories (100-101)
  if (categoryId === 100 || categoryId === 101) {
    return true;
  }
  
  return false;
};

/**
 * Check if a category ID represents a Testament category (backward compatibility)
 * Now also includes Church History categories that have subcategories
 * @param categoryId - Database category ID
 * @returns boolean indicating if it's a main category with subcategories
 */
export const isTestamentCategory = (categoryId: number): boolean => {
  return isMainCategoryWithSubcategories(categoryId);
};

/**
 * Check if a category ID represents Old Testament
 * @param categoryId - Database category ID
 * @returns boolean indicating if it's Old Testament (ID 1)
 */
export const isOldTestament = (categoryId: number): boolean => {
  return categoryId === 1;
};

/**
 * Check if a category ID represents New Testament
 * @param categoryId - Database category ID
 * @returns boolean indicating if it's New Testament (ID 6)
 */
export const isNewTestament = (categoryId: number): boolean => {
  return categoryId === 6;
};

/**
 * Check if a category ID represents Early Church History
 * @param categoryId - Database category ID
 * @returns boolean indicating if it's Early Church History (ID 100)
 */
export const isEarlyChurchHistory = (categoryId: number): boolean => {
  return categoryId === 100;
};

/**
 * Check if a category ID represents Church Traditions & Movements
 * @param categoryId - Database category ID
 * @returns boolean indicating if it's Church Traditions & Movements (ID 101)
 */
export const isChurchTraditions = (categoryId: number): boolean => {
  return categoryId === 101;
};